import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircle, Phone, ChevronLeft, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import api from "@/lib/api";
import { PHONE_TEL, openWa } from "@/lib/wa";

export default function NeighborhoodPage() {
  const { slug } = useParams();
  const { t, pick } = useLang();
  const { zones, services, neighborhoods: allHoods } = useData();
  const [hood, setHood] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setHood(null);
    setNotFound(false);
    api.get(`/neighborhoods/${slug}`).then(({ data }) => setHood(data)).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 pt-[72px] text-center">
        <h1 className="text-2xl font-bold text-white">الحي غير موجود في قائمة التغطية</h1>
        <Link to="/riyadh" className="btn-gold">مناطق التغطية</Link>
      </main>
    );
  }

  if (!hood) return <div className="flex min-h-screen items-center justify-center pt-[72px]"><div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" /></div>;

  const name = pick(hood.name_ar, hood.name_en);
  const zoneLabel = pick((zones.find((z) => z.id === hood.zone) || {}).ar, (zones.find((z) => z.id === hood.zone) || {}).en) || hood.zone;
  const note = hood.status === "unavailable" ? t("riyadh.unavailNote") : hood.status === "available" ? t("riyadh.availNote") : t("riyadh.afterNote");
  const waMsg = t("riyadh.waMsg").replace("{hood}", name).replace("{service}", "____");
  const nearby = (allHoods || []).filter((n) => n.zone === hood.zone && n.slug !== hood.slug).slice(0, 6);

  return (
    <main>
      <SEO title={`بنشر متنقل في ${name} | ميرو للإطارات وخدمات السيارات`} description={`اطلب خدمات الإطارات والبنشر المتنقل وحساسات الكفرات في ${name} بالرياض من ميرو. أرسل موقعك عبر واتساب 0507359999 للتأكد من التغطية والتكلفة.`} path={`/neighborhood/${hood.slug}`} />
      <section className="relative overflow-hidden pt-32 sm:pt-40">
        <div className="pointer-events-none absolute -top-24 start-1/3 h-[360px] w-[360px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-5 pb-14 lg:px-8">
          <Link to="/riyadh" className="mb-6 inline-flex items-center gap-1.5 text-sm text-mist transition-colors hover:text-gold" data-testid="hood-back-link">
            <ChevronLeft size={15} className="rotate-180" style={{ transform: pick("none", "rotate(180deg)") }} /> {t("nav.coverage")}
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <p className="eyebrow"><MapPin size={13} className="inline" /> {zoneLabel}</p>
            <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold text-gold" data-testid="hood-page-status">{t(`status.${hood.status}`)}</span>
          </div>
          <h1 className="mt-4 text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl" data-testid="hood-page-title">
            بنشر متنقل في {name}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-9 text-mist">{note}</p>
          <div className="mt-8 flex flex-wrap gap-3.5">
            <button onClick={() => openWa(waMsg)} className="btn-wa" data-testid="hood-page-wa"><MessageCircle size={18} /> {t("riyadh.orderWa")}</button>
            <a href={PHONE_TEL} className="btn-gold" data-testid="hood-page-call"><Phone size={18} /> {t("cta.callUs")}</a>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-coal/40 py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="mb-8 text-2xl font-bold text-white" data-testid="hood-services-title">{t("riyadh.servicesTitle")}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {services.filter((s) => s.slug !== "other-services").map((s) => (
              <Reveal key={s.slug}>
                <button
                  onClick={() => openWa(t("riyadh.waMsg").replace("{hood}", name).replace("{service}", pick(s.title_ar, s.title_en)))}
                  className="group w-full rounded-2xl border border-white/10 bg-ink/70 p-6 text-start transition-all duration-300 hover:border-gold/40 hover:bg-coal"
                  data-testid={`hood-service-${s.slug}`}
                >
                  <h3 className="text-sm font-bold text-white group-hover:text-gold">{pick(s.title_ar, s.title_en)}</h3>
                  <p className="mt-2 text-[11px] leading-6 text-dim">{pick(s.short_ar, s.short_en)}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {nearby.length > 0 && (
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <h2 className="mb-8 text-xl font-bold text-white">{t("riyadh.nearby")}</h2>
            <div className="flex flex-wrap gap-3">
              {nearby.map((n) => (
                <Link key={n.slug} to={`/neighborhood/${n.slug}`} className="rounded-full border border-white/12 bg-coal/60 px-5 py-2.5 text-sm text-zinc-300 transition-all hover:border-gold/50 hover:text-gold" data-testid={`hood-nearby-${n.slug}`}>
                  {pick(n.name_ar, n.name_en)}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
